import React from "react";
import { Link } from "react-router-dom";
import styles from "../InverluxApps.module.css";

const TinyJpgPngPrivacy = () => (
  <section className={styles.policySection}>
    <div className={styles.policyWidth}>
      <p className={styles.eyebrow}>Tiny JPG &amp; PNG</p>
      <h1>Privacy Policy</h1>
      <p className={styles.policyDate}>
        Last updated: October 21, 2026. Publisher: Inverlux LLC.
      </p>
      <p className={styles.pageIntro}>
        This policy covers the Tiny JPG &amp; PNG Chrome extension, including
        Free and optional Pro. Pro payment and licensing are configured.
        The extension is still awaiting Chrome Web Store release, and its
        download status remains Coming soon. The practices below describe
        local image processing and the separate Pro services.
      </p>

      <div className={styles.policySummary}>
        <strong>Privacy summary</strong>
        <p>
          Image processing stays local on your computer. Images, pixel data,
          filenames, and image metadata are not uploaded. There are no ads or
          analytics, and Free requires no account. Optional Pro checkout and
          licensing use separate online services, not an image-processing server.
        </p>
      </div>

      <article className={styles.policyContent}>
        <h2>1. Images and local processing</h2>
        <p>
          The extension processes only the JPEG and PNG files you select or
          drop into its application tab. Compression runs locally and does
          not need a server. The extension does not access browsing history,
          inspect web-page content, or monitor browsing activity.
        </p>
        <p>
          Selected files and results are held for the current application tab.
          Removing results, starting a new batch, or closing the tab releases
          application references to those images. Your browser and operating
          system determine when the underlying memory is reclaimed. Previews
          use temporary local Blob URLs, not uploaded images.
        </p>

        <h2>2. Downloads and permissions</h2>
        <p>
          Saving an image or ZIP uses Chrome's Downloads system and your
          download preferences. The extension reads the status of downloads
          it initiates to report completion or interruption. Saved files
          remain on your computer until you remove them, and Chrome maintains
          its own download history. Selected source files are not overwritten.
        </p>
        <ul>
          <li>
            <code>downloads</code>: saves compressed images and ZIP batches,
            checks their download status, and offers Show in folder.
          </li>
          <li>
            <code>storage</code>: stores local installation and Pro licensing
            information described below. This storage is not synced.
          </li>
          <li>
            <code>downloads.open</code>: an optional permission requested only
            when you explicitly choose Open File for a completed download.
            Normal compression and saving do not require it.
          </li>
        </ul>
        <p>
          Configured Pro builds request access to their
          licensing host for activation and verification. This access is
          separate from image processing and does not grant access to the
          contents of other websites. JavaScript and compression WebAssembly
          are bundled with the extension, not downloaded as remote code.
        </p>

        <h2>3. Local extension storage</h2>
        <p>
          Local extension storage holds a random installation ID and, when
          Pro is activated, a signed entitlement, random device
          credential, and last verification-attempt time. It does not store
          images, filenames, compression history, purchase license keys,
          payment-card details, or provider API secrets.
        </p>
        <p>
          Uninstalling removes local extension storage but does not remove
          saved image downloads or server-side licensing records. Clearing
          storage or uninstalling does not automatically free a Pro device
          slot. Deactivate online first, or contact support for a lost-device
          reset.
        </p>

        <h2>4. Pro checkout</h2>
        <p>
          Buy Pro in the extension opens Lemon Squeezy's
          hosted checkout in a browser tab. Lemon Squeezy handles payment and
          receipt/customer details under its own{" "}
          <a href="https://www.lemonsqueezy.com/privacy">privacy policy</a>.
          Payment-card details do not enter the extension or Inverlux's
          licensing API. Checkout requests do not include your images or
          installation ID. No Inverlux application account is required.
        </p>
        <p>
          Pro costs $14.99 USD one-time, with no subscription and permanent
          Pro access for 3 active Chrome installations. A 30-day refund-request
          window applies from the date of purchase. Refund requests go through
          support@inverlux.com using the Pro ownership-verification process
          described below. The extension is still awaiting Chrome Web Store release.
        </p>

        <h2>5. Pro licensing and network data</h2>
        <p>
          Activation sends your entered purchase key and installation ID
          over HTTPS to Inverlux's licensing service at{" "}
          <code>licenses.inverlux.com</code>, hosted on AWS in Oregon, United
          States (<code>us-west-2</code>). The service verifies purchase and
          license status with Lemon Squeezy. Subsequent verification and
          deactivation requests send the installation ID and a random device
          credential. These requests never contain images, filenames, or
          compression history.
        </p>
        <p>
          An IP address is necessarily visible during a connection. The
          licensing application stores only a keyed hash (HMAC) of it in
          temporary rate-limit counters, not the raw IP address, and does not
          use it for geolocation tracking.
        </p>
        <p>
          The license database stores provider order, license, and
          activation identifiers; installation IDs; hashed device credentials;
          and status timestamps. It does not store plaintext purchase keys,
          customer names or emails, or provider response bodies. Provider
          responses may contain customer details transiently during
          verification. Authenticated refund notifications are reduced to
          status records and a digest; raw notification bodies are not retained.
        </p>
        <p>
          Pro normally revalidates after 30 days while the application is
          open, with 14 additional days of offline access. After that, Free
          remains usable until online verification succeeds. An entirely
          offline installation cannot receive an immediate refund or
          revocation update. Image processing always stays local.
        </p>

        <h2>6. Server retention</h2>
        <p>
          The following production retention schedule applies to the
          Pro licensing service, not to your locally processed images:
        </p>
        <ul>
          <li>
            Operational logs: 14 days. Application request/body logging and
            AWS API access logging are disabled; keys and credentials are not
            written to application logs.
          </li>
          <li>
            Inactive, pending, and deactivated device records: eligible for
            automatic deletion 90 days after their last update. Successful
            activation clears the device-record expiry.
          </li>
          <li>Refund-notification digests: eligible for deletion after 90 days.</li>
          <li>Temporary rate counters and locks: short-lived automatic expiry.</li>
          <li>
            Necessary active-license bindings and license/order status,
            including refund-revocation records: retained while the licensing
            service operates.
          </li>
          <li>
            Recovery backups: up to 35 days. Backup copies of deleted records
            may remain within that recovery window.
          </li>
        </ul>
        <p>
          Automatic deletion is asynchronous. An expiry or 90-day eligibility
          date is not a guarantee that a record is deleted at that exact time.
          AWS and Lemon Squeezy may retain their own operational or payment
          records under their respective policies and applicable requirements.
        </p>

        <h2>7. Privacy and deletion requests</h2>
        <p>
          Email <a href="mailto:support@inverlux.com">support@inverlux.com</a>{" "}
          for privacy questions or deletion requests. An order number is
          required only for Pro-related requests; Free users do not need one.
          Do not email images, license keys, device credentials, payment-card
          details, or identity documents.
        </p>
        <p>
          For Pro-related requests, we verify purchaser ownership through the
          email address already associated with the order in Lemon Squeezy.
          A newly supplied email address or an order number alone is not
          proof of ownership. We explain any effect on active access and
          installation slots before making changes.
        </p>
        <p>
          After verification, we remove eligible records and retain necessary
          active-license and refund-revocation records while the licensing
          service operates. Where appropriate, we use the provider's supported
          deactivation or reset process; deleting a database record alone
          does not free a provider device slot. We explain what was removed,
          what remains and why, and the up-to-35-day recovery-backup limitation.
          Provider-held payment records follow the provider's policies and
          applicable requirements.
        </p>
        <p>
          There is no server copy of your images for Inverlux to delete.
          Remove results or close the application tab to release its image
          references, and delete saved downloads yourself. If you contact
          support, we receive your email address and the message you choose
          to send and use them to handle your request.
        </p>

        <h2>8. Use, sharing, and security</h2>
        <p>
          Inverlux does not receive, sell, or share image data through this
          extension. The extension has no advertising, analytics, or tracking.
          Pro service data is used for checkout, license activation,
          verification, refunds, service security, and related support, not
          advertising or profiling. AWS provides licensing infrastructure;
          Lemon Squeezy provides checkout and license/payment verification.
        </p>
        <p>
          Information obtained through Chrome APIs is used only to provide
          the described compression, download, and entitlement features, in
          compliance with the{" "}
          <a href="https://developer.chrome.com/docs/webstore/program-policies/limited-use">
            Chrome Web Store User Data Policy, including its Limited Use requirements
          </a>.
          Licensing requests use HTTPS. Protect your browser, device, and
          purchase credentials; local processing does not replace those
          security precautions.
        </p>

        <h2>9. Changes and contact</h2>
        <p>
          Inverlux LLC will update this page and its last-updated date when
          data practices change. This policy and the Chrome Web Store
          disclosures will be kept up to date to match the final released
          configuration.
        </p>
        <p>
          Privacy and support contact: Inverlux LLC, {" "}
          <a href="mailto:support@inverlux.com">support@inverlux.com</a>.
        </p>
      </article>

      <div className={styles.policyLinks}>
        <Link to="/tiny-jpg-png">Back to Tiny JPG &amp; PNG</Link>
        <Link to="/support">Contact support</Link>
      </div>
    </div>
  </section>
);

export default TinyJpgPngPrivacy;
